import React from 'react';
import {
  Box,
  Typography,
  Link,
  IconButton,
  Tooltip,
  // PaperProps // Will be imported as type
} from '@mui/material';
import type { PaperProps } from '@mui/material'; // Type-only import
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Define a type for Referral Codes (can be imported if defined elsewhere)
interface ReferralCode {
  code: string;
  status: 'available' | 'used' | 'subscription_paid';
  createdAt: string;
  // link?: string; // If you plan to generate full links
}

interface AppUser {
    id: string;
    googleId: string;
    email: string;
    name: string;
    picture?: string;
    isBetaUser?: boolean; 
    apiKey?: string; 
}
  
interface ReferralLinksProps {
  referralCodes: ReferralCode[];
  currentUser: AppUser | null; // Needed for generating the full referral link base URL
  showSnackbar: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  PaperComponent?: React.ElementType<PaperProps>; // Optional: if you want to wrap in Paper
  sx?: PaperProps['sx']; // Optional: sx props for the wrapping Box or Paper
}

const ReferralLinks: React.FC<ReferralLinksProps> = ({ referralCodes, currentUser, showSnackbar, PaperComponent, sx }) => {
  if (!currentUser) {
    return null; // Or some placeholder if currentUser is strictly required for other reasons
  }

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const content = (
    <Box sx={{ width: '100%', ...(PaperComponent ? {} : sx) }}>
      <Typography sx={{ fontWeight: 500, mb: 1 }}>Your Referral Links:</Typography>
      {referralCodes && Array.isArray(referralCodes) && referralCodes.length > 0 ? (
        referralCodes.map((refCode, index) => {
          let icon = null;
          let linkColor = 'text.primary';
          let backgroundColor = 'rgba(0, 0, 0, 0.05)';
          const referralLink = `${baseUrl}/?c=${refCode.code}`;
          let tooltipTitle = "Invite and Earn $5"; // Default title

          if (refCode.status === 'used') {
            icon = <HelpOutlineIcon sx={{ color: 'goldenrod' }} />; // Changed to goldenrod for better visibility on yellow
            backgroundColor = 'rgba(255, 215, 0, 0.1)'; // Yellowish background
            tooltipTitle = "Waiting for user subscription to earn $5";
          } else if (refCode.status === 'subscription_paid') {
            icon = <CheckCircleOutlineIcon sx={{ color: 'green' }} />;
            backgroundColor = 'rgba(0, 128, 0, 0.1)';
            tooltipTitle = "Earned $5";
          } else if (refCode.status === 'available') {
            linkColor = 'text.secondary';
          }

          return (
            <Tooltip title={tooltipTitle} arrow key={refCode.code || `ref-${index}-tooltip`}>
              <Box
                key={refCode.code || `ref-${index}`}
                onClick={async () => {
                  if (refCode.status === 'available') {
                    const shareData = {
                      title: 'UnitAI Referral Code',
                      text: `I'm using UnitAI.net AI agent for Unity development now, I invite you to join. You can join by clicking this link:`,
                      url: referralLink,
                    };
                    try {
                      if (navigator.share) {
                        await navigator.share(shareData);
                        showSnackbar(`Referral link for ${refCode.code} shared!`, 'success');
                      } else {
                        await navigator.clipboard.writeText(referralLink);
                        showSnackbar(`Referral link for ${refCode.code} copied to clipboard (share not supported)!`, 'info');
                      }
                    } catch (err) {
                      console.error('Error sharing or copying referral link:', err);
                      try {
                        await navigator.clipboard.writeText(referralLink);
                        showSnackbar(`Referral link for ${refCode.code} copied to clipboard!`, 'success');
                      } catch (copyErr) {
                        console.error('Error copying referral link to clipboard after share failed:', copyErr);
                        showSnackbar('Could not share or copy the referral link.', 'error');
                      }
                    }
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: backgroundColor,
                  ...(refCode.status === 'available' && {
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)', // Or a lighter/darker shade of the current backgroundColor
                    },
                  }),
                }}
              >
                {refCode.status === 'used' ? (
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      textDecoration: 'none',
                    }}
                  >
                    {refCode.code}
                  </Typography>
                ) : (
                  <Link
                    href={referralLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: 'monospace',
                      color: linkColor,
                      textDecoration: refCode.status === 'available' ? 'none' : 'underline',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    {refCode.code}
                  </Link>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {icon && <Box sx={{ mr: 1 }}>{icon}</Box>}
                  {refCode.status === 'available' && (
                    <IconButton
                      size="small"
                      aria-label={`Share referral link ${refCode.code}`}
                      title="Share referral link"
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })
      ) : (
        <Typography sx={{ color: 'grey.600', fontStyle: 'italic' }}>
          You currently have no referral codes.
        </Typography>
      )}
    </Box>
  );

  if (PaperComponent) {
    return <PaperComponent sx={sx}>{content}</PaperComponent>;
  }

  return content;
};

export default ReferralLinks; 